/**
 * arbi status
 *
 * Shows current config + login state (no API calls).
 * `--json` returns a machine-readable object for agent scripting —
 * this is the recommended way for an AI agent to check whether the
 * CLI is configured and logged in before attempting other calls.
 */

import { Command } from 'commander'
import { getConfig, getCredentials } from '../config.js'
import { getCachedWorkspaceName } from '../completion-cache.js'
import { label, dim } from '../output.js'

export function registerStatusCommand(program: Command): void {
  program
    .command('status')
    .description('Show current configuration and login status')
    .option('--json', 'Output as JSON for scripting')
    .action((opts: { json?: boolean }) => {
      const config = getConfig()
      const creds = getCredentials()
      const wsId = config?.selectedWorkspaceId ?? null
      // Workspace name comes from the completion cache (populated after any
      // `arbi workspaces` / login call). `status` itself stays offline so it
      // remains usable when the network is down.
      const wsName = wsId ? getCachedWorkspaceName(wsId) : null

      if (opts.json) {
        console.log(
          JSON.stringify(
            {
              configured: !!config?.baseUrl,
              server: config?.baseUrl ?? null,
              logged_in: !!creds,
              user: creds?.email ?? null,
              workspace: wsId,
              workspace_name: wsName,
            },
            null,
            2
          )
        )
        return
      }

      if (!config?.baseUrl) {
        dim('Not configured. Run: arbi config set-url <url>')
        return
      }

      label('Server:', config.baseUrl)

      if (creds) {
        label('User:', creds.email)
      } else {
        label('User:', '(not logged in)')
      }

      if (wsId) {
        label('Workspace:', wsName ? `${wsName} (${wsId})` : wsId)
      } else {
        label('Workspace:', '(none selected)')
      }
    })
}
